import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Users, Mail, Phone, DollarSign, Calendar, TrendingUp, Search, Filter, Download, Upload, Edit2, X, Plus, Trash2, MapPin, Home, Wind, Ruler, Send, CheckCircle, AlertTriangle, Loader, Code, Clock, RefreshCw } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import AdminNewAppointment from './AdminNewAppointment';

// Public booking page. /alltech-scheduler/ does not exist and 404s.
const SCHEDULER_LINK = 'https://alltechllc.org/scheduler/';

/**
 * Attaches an image to an outgoing text (MMS).
 *
 * The image has to live at a public https URL because Twilio's servers fetch it
 * rather than the phone, so the file is uploaded to our own site first and the
 * returned URL is what gets sent.
 */
const SmsImagePicker = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [notice, setNotice] = useState('');

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // allow re-picking the same file after an error
    if (!file) return;

    setUploadError('');
    setNotice('');
    setUploading(true);

    try {
      const body = new FormData();
      body.append('image', file);

      const response = await fetch('/scheduler/api/upload-mms-image.php', {
        method: 'POST',
        body
      });
      const result = await response.json();

      if (result.success) {
        onChange(result.url);
        if (result.oversized) setNotice(result.message);
      } else {
        setUploadError(result.error || 'Upload failed');
      }
    } catch (err) {
      setUploadError('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mt-3 border border-gray-200 rounded-lg p-3 bg-gray-50">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">Picture message (optional)</span>
        {value && (
          <button
            type="button"
            onClick={() => { onChange(''); setNotice(''); setUploadError(''); }}
            className="text-xs text-red-600 hover:text-red-700"
          >
            Remove image
          </button>
        )}
      </div>

      {value ? (
        <div className="flex items-start gap-3">
          <img
            src={value}
            alt="Attached to text message"
            className="w-24 h-24 object-cover rounded border border-gray-300 bg-white"
          />
          <div className="min-w-0">
            <p className="text-xs text-gray-600 break-all">{value}</p>
            <p className="text-xs text-gray-500 mt-1">
              Sends as an MMS. Costs more per message than plain text.
            </p>
          </div>
        </div>
      ) : (
        <label className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded cursor-pointer bg-white border border-gray-300 hover:bg-gray-100">
          <Upload className="w-4 h-4" />
          {uploading ? 'Uploading...' : 'Choose an image'}
          <input
            type="file"
            accept="image/jpeg,image/png,image/gif"
            onChange={handleFile}
            disabled={uploading}
            className="hidden"
          />
        </label>
      )}

      {!value && (
        <p className="text-xs text-gray-500 mt-2">
          JPG, PNG, or GIF. Under 600KB works best - carriers compress MMS heavily.
        </p>
      )}
      {notice && <p className="text-xs text-amber-700 mt-2">{notice}</p>}
      {uploadError && <p className="text-xs text-red-600 mt-2">{uploadError}</p>}
    </div>
  );
};

const AdminCustomers = (props) => {
  // Destructure props with defaults to prevent undefined errors
  const {
    createCustomer,
    updateCustomer,
    deleteCustomer
  } = props;

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [appointmentAddresses, setAppointmentAddresses] = useState([]);
  const [customerACUnits, setCustomerACUnits] = useState([]);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddCustomerForm, setShowAddCustomerForm] = useState(false);
  const [showEditCustomerForm, setShowEditCustomerForm] = useState(false);
  const [showBulkMessage, setShowBulkMessage] = useState(false);
  const [bulkStep, setBulkStep] = useState('compose'); // compose, confirm, sending, results
  const [bulkForm, setBulkForm] = useState({ subject: '', message: '', send_email: true, send_sms: false, sms_media_url: '' });
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkProgress, setBulkProgress] = useState(null); // { done, total }
  // When set, the compose modal is retrying one earlier send instead of
  // mailing the whole list: { batch_id, failed, channels }.
  const [retryTarget, setRetryTarget] = useState(null);
  const [consentSavingId, setConsentSavingId] = useState(null);
  const [htmlMode, setHtmlMode] = useState(false);
  const [showSingleMessage, setShowSingleMessage] = useState(false);
  const [singleMessageTarget, setSingleMessageTarget] = useState(null);
  const [singleForm, setSingleForm] = useState({ subject: '', message: '', send_email: true, send_sms: false, sms_media_url: '' });
  const [singleStep, setSingleStep] = useState('compose');
  const [singleResults, setSingleResults] = useState(null);
  const [singleHtmlMode, setSingleHtmlMode] = useState(false);
  const [showMessageLog, setShowMessageLog] = useState(false);
  const [messageLogBatches, setMessageLogBatches] = useState([]);
  const [messageLogLoading, setMessageLogLoading] = useState(false);
  const [messageLogError, setMessageLogError] = useState('');
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [batchDetail, setBatchDetail] = useState([]);
  const [batchDetailLoading, setBatchDetailLoading] = useState(false);

  // Customer picked for "Book Appointment" — the new-appointment form takes over
  // the panel until it is closed.
  const [bookingCustomer, setBookingCustomer] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importStep, setImportStep] = useState('select'); // select, preview, importing, results
  const [importRows, setImportRows] = useState([]);
  const [importFileName, setImportFileName] = useState('');
  const [importResults, setImportResults] = useState(null);
  const [importError, setImportError] = useState('');

  useEffect(() => {
    // Debug: Log what functions are available
    console.log('AdminCustomers Props Check:', {
      hasCreateCustomer: typeof createCustomer,
      hasUpdateCustomer: typeof updateCustomer,
      hasDeleteCustomer: typeof deleteCustomer,
      allProps: Object.keys(props)
    });
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await fetch('/scheduler/api/customers.php');
      const result = await response.json();

      if (result.success) {
        setCustomers(result.data);
        setError(null);
      } else {
        setError(result.error || 'Failed to load customers');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Error fetching customers:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerAddresses = async (customerEmail) => {
    // These records are keyed by email; a customer without one has none, and
    // querying for it would send the literal string "null".
    if (!customerEmail) { setCustomerAddresses([]); return; }
    try {
      const response = await fetch(`/scheduler/api/customer-addresses.php?customer_email=${encodeURIComponent(customerEmail)}`);
      const result = await response.json();

      if (result.success) {
        setCustomerAddresses(result.data);
      }
    } catch (err) {
      console.error('Error fetching addresses:', err);
    }
  };

  const fetchAppointmentAddresses = async (customerEmail) => {
    if (!customerEmail) { setAppointmentAddresses([]); return; }
    try {
      const response = await fetch(`/scheduler/api/appointments.php?customer_email=${encodeURIComponent(customerEmail)}`);
      const result = await response.json();

      if (result.success) {
        // Extract unique addresses from appointments
        const uniqueAddresses = [];
        const seen = new Set();

        result.data.forEach(appointment => {
          if (appointment.service_address) {
            const addressKey = appointment.service_address.toLowerCase().trim();
            if (!seen.has(addressKey)) {
              seen.add(addressKey);
              uniqueAddresses.push({
                address: appointment.service_address,
                appointment_date: appointment.appointment_date,
                service_name: appointment.service_name
              });
            }
          }
        });

        setAppointmentAddresses(uniqueAddresses);
      }
    } catch (err) {
      console.error('Error fetching appointment addresses:', err);
    }
  };

  const fetchCustomerACUnits = async (customerEmail) => {
    if (!customerEmail) { setCustomerACUnits([]); return; }
    try {
      const response = await fetch(`/scheduler/api/ac-units.php?customer_email=${encodeURIComponent(customerEmail)}`);
      const result = await response.json();

      if (result.success) {
        setCustomerACUnits(result.data || []);
      }
    } catch (err) {
      console.error('Error fetching AC units:', err);
    }
  };

  const handleAddNewCustomer = async (customerData) => {
    // Check if createCustomer function exists
    if (!createCustomer || typeof createCustomer !== 'function') {
      console.error('❌ createCustomer function is not available!');
      console.error('Available props:', Object.keys(props));
      alert('Error: createCustomer function is not available. Please check the console for details.');
      return;
    }

    setIsSubmitting(true);
    try {
      console.log('📤 Calling createCustomer with:', customerData);
      const result = await createCustomer(customerData);
      console.log('📥 createCustomer result:', result);

      if (result && result.success) {
        await fetchCustomers();
        setShowAddCustomerForm(false);
        alert('Customer added successfully!');
      } else {
        alert('Failed to add customer: ' + (result?.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('💥 Error adding customer:', error);
      alert('Error adding customer: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateExistingCustomer = async (customerData) => {
    setIsSubmitting(true);
    try {
      const result = await updateCustomer(customerData);
      if (result && result.success) {
        await fetchCustomers();
        setShowEditCustomerForm(false);
        setEditingCustomer(null);
        alert('Customer updated successfully!');
      } else {
        alert('Failed to update customer: ' + (result?.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating customer:', error);
      alert('Error updating customer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCustomer = async (customer) => {
    if (!window.confirm(`Are you sure you want to delete ${customer.customer_name}?\n\nThis will permanently remove them from the database.`)) {
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await deleteCustomer(customer.id);
      if (result && result.success) {
        await fetchCustomers();
        alert('Customer deleted successfully!');
      } else {
        alert('Failed to delete customer: ' + (result?.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
      alert('Error deleting customer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditCustomer = async (customer) => {
    setEditingCustomer(customer);
    // Fetch all customer data
    await Promise.all([
      fetchCustomerAddresses(customer.customer_email),
      fetchAppointmentAddresses(customer.customer_email),
      fetchCustomerACUnits(customer.customer_email)
    ]);
    setShowAddressForm(false);
    setEditingAddress(null);
  };

  const handleSaveAddress = async (addressData) => {
    setIsSubmitting(true);
    try {
      const url = '/scheduler/api/customer-addresses.php';
      const method = editingAddress ? 'PUT' : 'POST';

      const payload = {
        ...addressData,
        customer_email: editingCustomer.customer_email,
        customer_name: editingCustomer.customer_name
      };

      if (editingAddress) {
        payload.id = editingAddress.id;
      }

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.success) {
        await fetchCustomerAddresses(editingCustomer.customer_email);
        setShowAddressForm(false);
        setEditingAddress(null);
        alert(editingAddress ? 'Address updated successfully!' : 'Address added successfully!');
      } else {
        alert('Error saving address: ' + result.error);
      }
    } catch (err) {
      alert('Error saving address');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;

    try {
      const response = await fetch(`/scheduler/api/customer-addresses.php?id=${addressId}`, {
        method: 'DELETE'
      });

      const result = await response.json();

      if (result.success) {
        await fetchCustomerAddresses(editingCustomer.customer_email);
        alert('Address deleted successfully!');
      } else {
        alert('Error deleting address');
      }
    } catch (err) {
      alert('Error deleting address');
      console.error(err);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTotalStats = () => {
    // Use appointments prop filtered to current year for accurate stats
    const currentYear = new Date().getFullYear();
    const yearAppointments = (props.appointments || []).filter(apt => {
      const aptDate = new Date(apt.appointment_date + 'T00:00:00');
      return aptDate.getFullYear() === currentYear;
    });

    return {
      totalCustomers: customers.length,
      totalRevenue: yearAppointments
        .filter(apt => apt.payment_status === 'paid')
        .reduce((sum, apt) => sum + (apt.payment_amount || apt.final_price || apt.service_price || 0), 0),
      totalAppointments: yearAppointments.length,
      totalPending: yearAppointments
        .filter(apt => apt.status === 'completed' && apt.payment_status === 'pending')
        .reduce((sum, apt) => sum + (apt.final_price || apt.service_price || 0), 0)
    };
  };

  const filteredCustomers = customers
    .filter(customer => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return (
        (customer.customer_name || '').toLowerCase().includes(search) ||
        (customer.customer_email || '').toLowerCase().includes(search) ||
        (customer.customer_phone || '').toLowerCase().includes(search)
      );
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'recent':
          return new Date(b.last_booking_date) - new Date(a.last_booking_date);
        case 'name':
          return a.customer_name.localeCompare(b.customer_name);
        case 'revenue':
          return (b.total_revenue || 0) - (a.total_revenue || 0);
        case 'appointments':
          return (b.total_appointments || 0) - (a.total_appointments || 0);
        default:
          return 0;
      }
    });

  const bulkQuillRef = useRef(null);
  const singleQuillRef = useRef(null);

  // Quill's stock image button embeds the file as a base64 data URI. That makes
  // the message enormous, and Gmail and Outlook routinely strip base64 <img>
  // tags outright, so the picture the customer sees is a broken box. Upload it
  // and insert a real URL instead.
  //
  // When the same send is also going out by text, the uploaded image doubles as
  // the MMS attachment unless one was already chosen - inserting a graphic into
  // the message is what people mean by "add a picture to the text".
  const makeImageHandler = (quillRef, setForm) => () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/jpeg,image/png,image/gif');
    input.click();

    input.onchange = async () => {
      const file = input.files && input.files[0];
      if (!file) return;

      const editor = quillRef.current && quillRef.current.getEditor();
      if (!editor) return;

      const range = editor.getSelection(true) || { index: editor.getLength() };
      editor.insertText(range.index, 'Uploading image...');

      try {
        const body = new FormData();
        body.append('image', file);

        const response = await fetch('/scheduler/api/upload-mms-image.php', {
          method: 'POST',
          body
        });
        const result = await response.json();

        editor.deleteText(range.index, 'Uploading image...'.length);

        if (result.success) {
          editor.insertEmbed(range.index, 'image', result.url);
          editor.setSelection(range.index + 1);
          setForm(f => (f.send_sms && !f.sms_media_url) ? { ...f, sms_media_url: result.url } : f);
        } else {
          window.alert(result.error || 'Image upload failed');
        }
      } catch (err) {
        editor.deleteText(range.index, 'Uploading image...'.length);
        window.alert('Image upload failed: ' + err.message);
      }
    };
  };

  const quillToolbar = [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'align': [] }],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    ['link', 'image'],
    ['clean']
  ];

  // One modules object per editor - each needs a handler bound to its own ref.
  // Memoized with stable deps because ReactQuill re-initializes when it changes.
  const bulkQuillModules = useMemo(() => ({
    toolbar: {
      container: quillToolbar,
      handlers: { image: makeImageHandler(bulkQuillRef, setBulkForm) }
    }
  }), []); // eslint-disable-line react-hooks/exhaustive-deps

  const singleQuillModules = useMemo(() => ({
    toolbar: {
      container: quillToolbar,
      handlers: { image: makeImageHandler(singleQuillRef, setSingleForm) }
    }
  }), []); // eslint-disable-line react-hooks/exhaustive-deps

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'align', 'list',
    'link', 'image'
  ];

  // Whenever email is on, the message body is HTML - the rich editor and the
  // "HTML Code" box both hold markup - so the link has to be an <a> tag. Only
  // the SMS-only box holds plain text. Either way the URL survives to the text
  // message: toSmsText() on the server unwraps <a href> links.
  //
  // The "already added" test has to match the booking URL only where it ENDS a
  // URL. Uploaded images live at .../scheduler/uploads/mms/<file>, which contains
  // the booking link as a prefix - a plain substring test treated any message
  // holding an image as already having the link, so the button silently did
  // nothing. The lookahead rejects a match with more path after it.
  const insertSchedulerLink = (setForm, isHtmlBody) => {
    const escaped = SCHEDULER_LINK.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const alreadyLinked = new RegExp(escaped + '(?![\\w/.-])');

    setForm(f => {
      if (alreadyLinked.test(f.message || '')) return f;
      const addition = isHtmlBody
        ? `<p><a href="${SCHEDULER_LINK}">Book your appointment</a></p>`
        : `\n\nBook your appointment: ${SCHEDULER_LINK}`;
      return { ...f, message: (f.message || '') + addition };
    });
  };

  // An image on its own is a real message - especially for a picture text, where
  // the graphic carries everything. Stripping tags and checking for leftover
  // text called an image-only body empty and disabled the Review button.
  const hasMessageContent = (form) => {
    const message = form.message || '';
    if (message.replace(/<[^>]*>/g, '').trim()) return true;
    if (/<img\b/i.test(message)) return true;
    return !!(form.send_sms && form.sms_media_url);
  };

  const loadMessageLog = async () => {
    setMessageLogLoading(true);
    setMessageLogError('');
    try {
      const response = await fetch('/scheduler/api/message-log.php');
      const result = await response.json();
      if (result.success) {
        setMessageLogBatches(result.data || []);
      } else {
        setMessageLogError(result.error || 'Could not load message history');
      }
    } catch (err) {
      setMessageLogError('Could not load message history: ' + err.message);
    } finally {
      setMessageLogLoading(false);
    }
  };

  const openMessageLog = () => {
    setShowMessageLog(true);
    setExpandedBatch(null);
    setBatchDetail([]);
    loadMessageLog();
  };

  const toggleBatch = async (batchId) => {
    if (expandedBatch === batchId) {
      setExpandedBatch(null);
      return;
    }
    setExpandedBatch(batchId);
    setBatchDetail([]);
    setBatchDetailLoading(true);
    try {
      const response = await fetch(`/scheduler/api/message-log.php?batch_id=${encodeURIComponent(batchId)}`);
      const result = await response.json();
      setBatchDetail(result.success ? (result.data || []) : []);
    } catch (err) {
      setBatchDetail([]);
    } finally {
      setBatchDetailLoading(false);
    }
  };

  // Twilio reports progress asynchronously, so a fresh send starts as "pending"
  // and fills in over the following seconds as callbacks arrive.
  const statusStyle = (status) => {
    if (status === 'delivered') return 'bg-green-100 text-green-800';
    if (status === 'undelivered' || status === 'failed') return 'bg-red-100 text-red-700';
    if (status === 'sent') return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-600';
  };

  const openBulkMessage = () => {
    setShowBulkMessage(true);
    setBulkStep('compose');
    setBulkForm({ subject: '', message: '', send_email: true, send_sms: false, include_non_consented: false, sms_media_url: '' });
    setBulkResults(null);
    setBulkProgress(null);
    setRetryTarget(null);
  };

  // Resend one earlier batch to only the recipients it failed to reach. The log
  // keeps the subject but not the body, so the message has to be composed
  // again; what this saves is mailing the people who already received it.
  const openRetryFailed = (batch) => {
    const channels = [];
    if (batch.email_count > 0) channels.push('email');
    if (batch.sms_count > 0) channels.push('sms');

    setShowMessageLog(false);
    setShowBulkMessage(true);
    setBulkStep('compose');
    setBulkForm({
      subject: '',
      message: '',
      send_email: channels.includes('email'),
      send_sms: channels.includes('sms'),
      include_non_consented: false,
      sms_media_url: ''
    });
    setBulkResults(null);
    setBulkProgress(null);
    setRetryTarget({ batch_id: batch.batch_id, failed: batch.failed, started_at: batch.started_at });
  };

  // The server processes a small batch per request so it cannot hit the 30s
  // execution limit. Keep POSTing with the returned next_offset until done,
  // accumulating the per-batch tallies as we go.
  const sendBulkMessage = async () => {
    setBulkStep('sending');
    setBulkProgress({ done: 0, total: 0 });

    const totals = {
      email: { sent: 0, failed: 0, skipped: 0, errors: [] },
      sms: { sent: 0, failed: 0, skipped: 0, errors: [] }
    };

    const accumulate = (channel, batch) => {
      if (!batch) return;
      totals[channel].sent += batch.sent || 0;
      totals[channel].failed += batch.failed || 0;
      totals[channel].skipped += batch.skipped || 0;
      (batch.errors || []).forEach((e) => {
        if (totals[channel].errors.length < 10) totals[channel].errors.push(e);
      });
    };

    let offset = 0;
    let totalCustomers = 0;

    // The send is split across several requests; one id ties their log rows
    // together so the history screen shows a single entry, not five.
    const batchId = 'send-' + Date.now().toString(36);

    try {
      // Bounded to keep a server bug from looping forever.
      for (let guard = 0; guard < 500; guard++) {
        const response = await fetch('/scheduler/api/bulk-message.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...bulkForm,
            is_html: bulkForm.send_email,
            batch_id: batchId,
            retry_batch_id: retryTarget ? retryTarget.batch_id : undefined,
            offset,
            batch_size: 20
          })
        });

        const result = await response.json();

        if (!result.success) {
          // Report the failure along with whatever already went out, so a
          // mid-send stop is never silent.
          setBulkResults({
            success: false,
            error: result.error || 'Send failed',
            partial: offset > 0,
            sent_before_failure: offset,
            results: totals
          });
          setBulkStep('results');
          return;
        }

        accumulate('email', result.results?.email);
        accumulate('sms', result.results?.sms);

        totalCustomers = result.total_customers || totalCustomers;
        offset = result.next_offset ?? offset;
        setBulkProgress({ done: Math.min(offset, totalCustomers), total: totalCustomers });

        if (result.done) break;
      }

      setBulkResults({
        success: true,
        results: totals,
        total_customers: totalCustomers,
        message: `Bulk message completed. ${totals.email.sent + totals.sms.sent} messages sent.`
      });
      setBulkStep('results');
    } catch (err) {
      setBulkResults({
        success: false,
        error: 'Network error: ' + err.message,
        partial: offset > 0,
        sent_before_failure: offset,
        results: totals
      });
      setBulkStep('results');
    }
  };

  // Record or withdraw SMS marketing consent for one customer.
  // Turning consent ON requires a source, because "how did they opt in?" is the
  // question a carrier or TCPA audit actually asks.
  const toggleSmsConsent = async (customer) => {
    const currentlyConsented = Number(customer.sms_marketing_consent) === 1;
    let source = '';

    if (!currentlyConsented) {
      const previouslyOptedOut = !!customer.sms_opted_out_at;
      const warning = previouslyOptedOut
        ? 'WARNING: this customer previously replied STOP. Only re-enable if they '
          + 'have since asked to receive texts again.\n\n'
        : '';

      source = window.prompt(
        `${warning}How did ${customer.customer_name} opt in to promotional texts?\n\n`
          + 'Examples: "Verbal consent in person 8/15/26", "Signed work order", "Asked by phone"',
        ''
      );
      if (source === null) return;          // cancelled
      if (!source.trim()) {
        alert('A consent source is required. Nothing was changed.');
        return;
      }
    } else if (!window.confirm(`Remove SMS marketing consent for ${customer.customer_name}?`)) {
      return;
    }

    setConsentSavingId(customer.id);
    try {
      const response = await fetch('/scheduler/api/customer-consent.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: customer.id,
          channel: 'sms',
          consent: !currentlyConsented,
          source: source.trim()
        })
      });
      const result = await response.json();

      if (!result.success) {
        alert(result.error || 'Failed to update consent');
        return;
      }

      setCustomers(prev => prev.map(c =>
        c.id === customer.id ? { ...c, ...result.customer } : c
      ));
    } catch (err) {
      alert('Network error: ' + err.message);
    } finally {
      setConsentSavingId(null);
    }
  };

  const openSingleMessage = (customer) => {
    setSingleMessageTarget(customer);
    setShowSingleMessage(true);
    setSingleStep('compose');
    setSingleForm({ subject: '', message: '', send_email: !!customer.customer_email, send_sms: false, sms_media_url: '' });
    setSingleResults(null);
    setSingleHtmlMode(false);
  };

  const sendSingleMessage = async () => {
    setSingleStep('sending');
    try {
      const response = await fetch('/scheduler/api/send-message.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...singleForm,
          is_html: singleForm.send_email,
          customer_email: singleMessageTarget.customer_email,
          customer_phone: singleMessageTarget.customer_phone,
          customer_name: singleMessageTarget.customer_name
        })
      });
      const result = await response.json();
      setSingleResults(result);
      setSingleStep('results');
    } catch (err) {
      setSingleResults({ success: false, error: 'Network error: ' + err.message });
      setSingleStep('results');
    }
  };

  const emailCustomerCount = customers.filter(c => c.customer_email && c.customer_email.includes('@')).length;
  // Only consented, non-opted-out customers can receive promotional texts, so
  // count those rather than everyone with a phone number on file.
  const smsCustomerCount = customers.filter(c =>
    c.customer_phone &&
    c.customer_phone.length >= 10 &&
    Number(c.sms_marketing_consent) === 1 &&
    !c.sms_opted_out_at
  ).length;

  // Reachable when the admin overrides the opt-in requirement. A STOP reply is
  // still excluded here, matching what the server enforces.
  const smsAllCount = customers.filter(c =>
    c.customer_phone &&
    c.customer_phone.length >= 10 &&
    !c.sms_opted_out_at
  ).length;

  // Parse CSV text into an array of objects keyed by header name.
  // Handles quoted fields, escaped quotes (""), and embedded commas/newlines.
  const parseCSV = (text) => {
    // Strip a leading UTF-8 BOM if present.
    if (text.charCodeAt(0) === 0xFEFF) {
      text = text.slice(1);
    }

    const rows = [];
    let field = '';
    let record = [];
    let inQuotes = false;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];

      if (inQuotes) {
        if (char === '"') {
          if (text[i + 1] === '"') {
            field += '"';
            i++; // skip the escaped quote
          } else {
            inQuotes = false;
          }
        } else {
          field += char;
        }
      } else if (char === '"') {
        inQuotes = true;
      } else if (char === ',') {
        record.push(field);
        field = '';
      } else if (char === '\n' || char === '\r') {
        // Handle CRLF: skip the \n that follows a \r
        if (char === '\r' && text[i + 1] === '\n') i++;
        record.push(field);
        rows.push(record);
        record = [];
        field = '';
      } else {
        field += char;
      }
    }
    // Flush the final field/record if the file does not end with a newline.
    if (field !== '' || record.length > 0) {
      record.push(field);
      rows.push(record);
    }

    if (rows.length < 2) return [];

    const headers = rows[0].map(h => h.trim().toLowerCase());
    const findCol = (...names) => headers.findIndex(h => names.includes(h));
    const nameIdx = findCol('name', 'customer name', 'full name');
    const emailIdx = findCol('email', 'e-mail', 'email address');
    const phoneIdx = findCol('phone', 'phone number', 'mobile');
    const addressIdx = findCol('address', 'service address', 'address line 1');

    const result = [];
    for (let r = 1; r < rows.length; r++) {
      const cells = rows[r];
      // Skip completely empty lines.
      if (cells.every(c => c.trim() === '')) continue;
      const get = (idx) => (idx >= 0 && idx < cells.length ? cells[idx].trim() : '');
      result.push({
        name: get(nameIdx),
        email: get(emailIdx),
        phone: get(phoneIdx),
        address: get(addressIdx)
      });
    }
    return result;
  };

  const openImportModal = () => {
    setImportRows([]);
    setImportFileName('');
    setImportResults(null);
    setImportError('');
    setImportStep('select');
    setShowImportModal(true);
  };

  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportError('');
    setImportFileName(file.name);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = parseCSV(event.target.result);
        // Keep only rows that have at least a phone or an email to identify the customer.
        const usable = parsed.filter(row => row.email || row.phone);
        if (usable.length === 0) {
          setImportError('No usable rows found. The file needs a "Name", "Phone" and/or "Email" column with data.');
          setImportStep('select');
          return;
        }
        setImportRows(usable);
        setImportStep('preview');
      } catch (err) {
        setImportError('Could not read the file: ' + err.message);
        setImportStep('select');
      }
    };
    reader.onerror = () => {
      setImportError('Failed to read the file.');
      setImportStep('select');
    };
    reader.readAsText(file);
    // Reset the input so selecting the same file again re-triggers onChange.
    e.target.value = '';
  };

  const runImport = async () => {
    setImportStep('importing');
    try {
      const response = await fetch('/scheduler/api/customers-import.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customers: importRows })
      });
      const result = await response.json();
      setImportResults(result);
      setImportStep('results');
      if (result.success) {
        await fetchCustomers();
      }
    } catch (err) {
      setImportResults({ success: false, error: 'Network error: ' + err.message });
      setImportStep('results');
    }
  };

  const exportCustomers = () => {
    const csvContent = [
      ['Name', 'Email', 'Phone', 'Total Appointments', 'Completed', 'Scheduled', 'Total Revenue', 'Total Paid', 'Pending Payment', 'First Appointment', 'Last Appointment'],
      ...filteredCustomers.map(c => [
        c.customer_name,
        c.customer_email,
        c.customer_phone,
        c.total_appointments,
        c.completed_appointments,
        c.scheduled_appointments,
        c.total_revenue?.toFixed(2),
        c.total_paid?.toFixed(2),
        c.total_pending?.toFixed(2),
        c.first_appointment_date,
        c.last_appointment_date
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customers-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const stats = getTotalStats();

  if (bookingCustomer) {
    return (
      <AdminNewAppointment
        services={props.services || []}
        appointments={props.appointments || []}
        coupons={props.coupons || []}
        bundles={props.bundles || []}
        blockedDates={props.blockedDates || []}
        customers={customers}
        initialCustomer={bookingCustomer}
        createAppointment={props.createAppointment}
        adminSettings={props.adminSettings || {}}
        onClose={() => setBookingCustomer(null)}
        onSuccess={() => {
          if (props.loadAppointments) props.loadAppointments();
          fetchCustomers();
        }}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          <p className="mt-4 text-gray-600">Loading customers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-600 font-medium">{error}</p>
        <button
          onClick={fetchCustomers}
          className="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Customers</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalCustomers}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">This Year's Revenue</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.totalRevenue)}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">This Year's Appointments</p>
              <p className="text-2xl font-bold text-blue-600">{stats.totalAppointments}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Payments</p>
              <p className="text-2xl font-bold text-orange-600">{formatCurrency(stats.totalPending)}</p>
            </div>
            <div className="bg-orange-100 p-3 rounded-full">
              <TrendingUp className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Customer List */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-800">Customer Database</h3>
          <div className="flex gap-2">
            <button
              onClick={() => setShowAddCustomerForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Customer
            </button>
            <button
              onClick={openMessageLog}
              className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <Clock className="w-4 h-4" />
              Message History
            </button>
            <button
              onClick={openBulkMessage}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              Message All
            </button>
            <button
              onClick={openImportModal}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              Import CSV
            </button>
            <button
              onClick={exportCustomers}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2"
          >
            <option value="recent">Most Recent</option>
            <option value="name">Name (A-Z)</option>
            <option value="revenue">Highest Revenue</option>
            <option value="appointments">Most Appointments</option>
          </select>
        </div>

        {/* Customer Cards */}
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {filteredCustomers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">
                {searchTerm ? 'No customers match your search' : 'No customers yet'}
              </p>
              <p className="text-gray-400 text-sm mt-2">
                {searchTerm ? 'Try a different search term' : 'Customers will appear here after their first appointment'}
              </p>
            </div>
          ) : (
            filteredCustomers.map((customer, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-green-100 rounded-full p-2">
                        <Users className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 text-lg">{customer.customer_name}</h4>
                        <div className="flex flex-wrap gap-3 text-sm text-gray-600 mt-1">
                          <div className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            <span className={customer.customer_email ? '' : 'italic text-gray-400'}>
                              {customer.customer_email || 'No email'}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            <span>{customer.customer_phone}</span>
                          </div>
                        </div>

                        {/* SMS marketing consent. Promotional texts go only to
                            customers showing "Texts OK" here. */}
                        <div className="flex items-center gap-2 mt-2">
                          {Number(customer.sms_marketing_consent) === 1 ? (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700"
                              title={
                                customer.sms_consent_source
                                  ? `Opted in via: ${customer.sms_consent_source}`
                                  : 'Opted in'
                              }
                            >
                              ✓ Texts OK
                            </span>
                          ) : customer.sms_opted_out_at ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              ✕ Opted out (STOP)
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              No text consent
                            </span>
                          )}

                          <button
                            onClick={() => toggleSmsConsent(customer)}
                            disabled={consentSavingId === customer.id}
                            className="text-xs text-purple-600 hover:text-purple-800 underline disabled:opacity-50"
                          >
                            {consentSavingId === customer.id
                              ? 'Saving...'
                              : Number(customer.sms_marketing_consent) === 1
                                ? 'Remove'
                                : 'Record opt-in'}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-4 mt-4">
                      {/* Appointment Stats */}
                      <div className="bg-blue-50 rounded-lg p-3">
                        <p className="text-xs text-gray-600 mb-1">Appointments</p>
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl font-bold text-blue-600">{customer.total_appointments}</span>
                          <span className="text-xs text-gray-500">total</span>
                        </div>
                        <div className="flex gap-3 mt-2 text-xs">
                          <span className="text-green-600">✓ {customer.completed_appointments} done</span>
                          <span className="text-blue-600">📅 {customer.scheduled_appointments} upcoming</span>
                        </div>
                      </div>

                      {/* Revenue Stats */}
                      <div className="bg-green-50 rounded-lg p-3">
                        <p className="text-xs text-gray-600 mb-1">Revenue</p>
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl font-bold text-green-600">{formatCurrency(customer.total_revenue)}</span>
                        </div>
                        <div className="flex gap-3 mt-2 text-xs">
                          <span className="text-green-600">💰 {formatCurrency(customer.total_paid)} paid</span>
                          {customer.total_pending > 0 && (
                            <span className="text-orange-600">⏳ {formatCurrency(customer.total_pending)} pending</span>
                          )}
                        </div>
                      </div>

                      {/* Date Stats */}
                      <div className="bg-purple-50 rounded-lg p-3">
                        <p className="text-xs text-gray-600 mb-1">Customer Since</p>
                        <div className="flex items-baseline gap-2">
                          <span className="text-lg font-bold text-purple-600">{formatDate(customer.first_appointment_date)}</span>
                        </div>
                        <div className="mt-2 text-xs text-gray-600">
                          Last visit: {formatDate(customer.last_appointment_date)}
                        </div>
                      </div>
                    </div>

                    {/* Payment Status Summary */}
                    {customer.pending_payment_appointments > 0 && (
                      <div className="mt-3 bg-orange-50 border border-orange-200 rounded-lg p-2 flex items-center gap-2">
                        <span className="text-orange-600 text-xs font-medium">
                          ⚠️ {customer.pending_payment_appointments} appointment{customer.pending_payment_appointments > 1 ? 's' : ''} with pending payment
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="ml-4 flex flex-col gap-2">
                    <button
                      onClick={() => {
                        setEditingCustomer(customer);
                        setShowEditCustomerForm(true);
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg transition-colors"
                      title="Edit Customer"
                      disabled={isSubmitting}
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setBookingCustomer({
                        ...customer,
                        // This list endpoint aliases the columns as
                        // customer_email / customer_phone; the booking form
                        // reads a customers row, so hand it those names.
                        email: customer.customer_email || '',
                        phone: customer.customer_phone || ''
                      })}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white p-2 rounded-lg transition-colors"
                      title="Book Appointment"
                      disabled={isSubmitting}
                    >
                      <Calendar className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleEditCustomer(customer)}
                      className="bg-purple-600 hover:bg-purple-700 text-white p-2 rounded-lg transition-colors"
                      title="Manage Addresses & Services"
                      disabled={isSubmitting}
                    >
                      <MapPin className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => openSingleMessage(customer)}
                      className="bg-green-600 hover:bg-green-700 text-white p-2 rounded-lg transition-colors"
                      title="Send Message"
                      disabled={isSubmitting}
                    >
                      <Send className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteCustomer(customer)}
                      className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-lg transition-colors"
                      title="Delete Customer"
                      disabled={isSubmitting}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Results Summary */}
        {searchTerm && (
          <div className="mt-4 text-sm text-gray-600 border-t pt-4">
            Showing {filteredCustomers.length} of {customers.length} customers
          </div>
        )}
      </div>

      {/* Message History */}
      {showMessageLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-800 text-white p-4 flex justify-between items-center rounded-t-xl">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Message History
              </h3>
              <div className="flex items-center gap-3">
                <button onClick={loadMessageLog} title="Refresh" className="text-white hover:text-gray-300">
                  <RefreshCw className={`w-4 h-4 ${messageLogLoading ? 'animate-spin' : ''}`} />
                </button>
                <button onClick={() => setShowMessageLog(false)} className="text-white hover:text-gray-300">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-3">
              {messageLogError && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 text-sm">
                  {messageLogError}
                </div>
              )}

              {messageLogLoading && messageLogBatches.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Loader className="w-6 h-6 animate-spin mx-auto mb-2" />
                  Loading...
                </div>
              )}

              {!messageLogLoading && !messageLogError && messageLogBatches.length === 0 && (
                <p className="text-gray-500 text-sm text-center py-8">
                  No sends recorded yet. Messages sent from now on will appear here.
                </p>
              )}

              {messageLogBatches.map(batch => (
                <div key={batch.batch_id} className="border border-gray-200 rounded-lg">
                  <button
                    onClick={() => toggleBatch(batch.batch_id)}
                    className="w-full text-left p-4 hover:bg-gray-50 rounded-lg"
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-800">
                          {new Date(batch.started_at.replace(' ', 'T')).toLocaleString()}
                        </p>
                        <p className="text-sm text-gray-600 truncate mt-0.5">
                          {batch.sample_body || '(no text)'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {batch.sms_count > 0 && `${batch.sms_count} text${batch.sms_count === 1 ? '' : 's'}`}
                          {batch.sms_count > 0 && batch.email_count > 0 && ' · '}
                          {batch.email_count > 0 && `${batch.email_count} email${batch.email_count === 1 ? '' : 's'}`}
                          {batch.has_media && ' · with image'}
                        </p>
                      </div>
                      <div className="flex gap-1.5 flex-shrink-0 flex-wrap justify-end">
                        {batch.delivered > 0 && (
                          <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-800">
                            {batch.delivered} delivered
                          </span>
                        )}
                        {batch.pending > 0 && (
                          <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">
                            {batch.pending} pending
                          </span>
                        )}
                        {batch.failed > 0 && (
                          <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700">
                            {batch.failed} failed
                          </span>
                        )}
                      </div>
                    </div>
                  </button>

                  {batch.failed > 0 && (
                    <div className="px-4 pb-3">
                      <button
                        onClick={() => openRetryFailed(batch)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded bg-purple-100 text-purple-700 hover:bg-purple-200"
                      >
                        <Send className="w-3 h-3" />
                        Resend to the {batch.failed} that failed
                      </button>
                    </div>
                  )}

                  {expandedBatch === batch.batch_id && (
                    <div className="border-t border-gray-200 p-4 bg-gray-50">
                      {batchDetailLoading ? (
                        <p className="text-sm text-gray-500 text-center py-3">Loading recipients...</p>
                      ) : (
                        <div className="space-y-1 max-h-[600px] overflow-y-auto">
                          {batchDetail.map(row => (
                            <div key={row.id} className="flex justify-between items-center gap-3 text-sm py-1.5 border-b border-gray-200 last:border-0">
                              <div className="min-w-0">
                                <span className="text-gray-800">{row.customer_name || 'Unknown'}</span>
                                <span className="text-gray-500 ml-2">{row.recipient}</span>
                                {row.error_message && (
                                  <span className="block text-xs text-red-600">{row.error_message}</span>
                                )}
                              </div>
                              <span className={`text-xs px-2 py-0.5 rounded flex-shrink-0 ${statusStyle(row.status)}`}>
                                {row.channel === 'email' ? 'email · ' : ''}{row.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {messageLogBatches.some(b => b.pending > 0) && (
                <p className="text-xs text-gray-500 text-center pt-2">
                  Carriers confirm delivery over the following seconds or minutes.
                  Hit refresh to update pending counts.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Message Modal */}
      {showBulkMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-purple-600 text-white p-4 flex justify-between items-center rounded-t-xl">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Send className="w-5 h-5" />
                {retryTarget ? 'Resend to Failed Recipients' : 'Message All Customers'}
              </h3>
              {bulkStep !== 'sending' && (
                <button onClick={() => setShowBulkMessage(false)} className="text-white hover:text-gray-200">
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            <div className="p-6">
              {/* Step 1: Compose */}
              {bulkStep === 'compose' && (
                <div className="space-y-4">
                  {retryTarget && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-800">
                      <p className="font-medium">
                        Retrying the send from {new Date(retryTarget.started_at.replace(' ', 'T')).toLocaleString()}
                      </p>
                      <p className="mt-1 text-purple-700">
                        Only the {retryTarget.failed} recipients that send could not reach will
                        get this message. Everyone it already reached is left alone. The message
                        text is not kept in the log, so compose it again below.
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Send via:</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={bulkForm.send_email}
                          onChange={e => setBulkForm(f => ({ ...f, send_email: e.target.checked }))}
                          className="w-4 h-4 text-purple-600 rounded"
                        />
                        <Mail className="w-4 h-4 text-gray-600" />
                        <span className="text-sm">Email ({emailCustomerCount})</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={bulkForm.send_sms}
                          onChange={e => setBulkForm(f => ({ ...f, send_sms: e.target.checked }))}
                          className="w-4 h-4 text-purple-600 rounded"
                        />
                        <Phone className="w-4 h-4 text-gray-600" />
                        <span className="text-sm">SMS ({smsCustomerCount} opted in)</span>
                      </label>
                    </div>
                    {bulkForm.send_sms && (
                      <div className="mt-2 space-y-2">
                        {smsCustomerCount === 0 && !bulkForm.include_non_consented && (
                          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                            No customers have opted in to promotional texts yet, so this send
                            will reach nobody. Use "Record opt-in" on a customer to log consent,
                            or check the box below to text everyone.
                          </p>
                        )}
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!bulkForm.include_non_consented}
                            onChange={e => setBulkForm(f => ({ ...f, include_non_consented: e.target.checked }))}
                            className="mt-0.5 w-4 h-4 text-purple-600 rounded"
                          />
                          <span className="text-xs text-gray-600">
                            Text all customers, including those who have not opted in
                            ({smsAllCount} with a phone number). Customers who replied STOP are
                            always excluded.
                          </span>
                        </label>
                      </div>
                    )}
                  </div>

                  {bulkForm.send_email && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                      <input
                        type="text"
                        value={bulkForm.subject}
                        onChange={e => setBulkForm(f => ({ ...f, subject: e.target.value }))}
                        placeholder="e.g. Special Offer from AllTech LLC"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  )}

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">Message</label>
                      {bulkForm.send_email && (
                        <button
                          type="button"
                          onClick={() => setHtmlMode(m => !m)}
                          className={`flex items-center gap-1 text-xs px-2 py-1 rounded font-medium ${htmlMode ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                        >
                          <Code className="w-3 h-3" />
                          {htmlMode ? 'Rich Editor' : 'HTML Code'}
                        </button>
                      )}
                    </div>
                    {bulkForm.send_email ? (
                      <div className="bulk-email-editor">
                        {htmlMode ? (
                          <textarea
                            value={bulkForm.message}
                            onChange={e => setBulkForm(f => ({ ...f, message: e.target.value }))}
                            placeholder="Paste your HTML code here..."
                            rows={12}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            style={{ minHeight: '250px', tabSize: 2 }}
                          />
                        ) : (
                          <ReactQuill
                            ref={bulkQuillRef}
                            theme="snow"
                            value={bulkForm.message}
                            onChange={val => setBulkForm(f => ({ ...f, message: val }))}
                            placeholder="Compose your email message..."
                            modules={bulkQuillModules}
                            formats={quillFormats}
                            style={{ minHeight: '200px', background: '#fff', borderRadius: '0 0 8px 8px' }}
                          />
                        )}
                        {bulkForm.send_sms && (
                          <p className="text-xs mt-1 text-orange-600">
                            Note: SMS will receive a plain-text version of this message.
                          </p>
                        )}
                      </div>
                    ) : (
                      <>
                        <textarea
                          value={bulkForm.message}
                          onChange={e => setBulkForm(f => ({ ...f, message: e.target.value }))}
                          placeholder="Type your message here..."
                          rows={6}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                        {bulkForm.send_sms && (
                          <p className={`text-xs mt-1 ${bulkForm.message.length > 160 ? 'text-orange-600' : 'text-gray-500'}`}>
                            {bulkForm.message.length}/160 characters {bulkForm.message.length > 160 ? `(${Math.ceil(bulkForm.message.length / 160)} SMS segments)` : ''}
                          </p>
                        )}
                      </>
                    )}

                    <button
                      type="button"
                      onClick={() => insertSchedulerLink(setBulkForm, bulkForm.send_email)}
                      className="mt-2 inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
                    >
                      <Calendar className="w-3 h-3" />
                      Add booking link
                    </button>

                    {bulkForm.send_sms && (
                      <SmsImagePicker
                        value={bulkForm.sms_media_url}
                        onChange={url => setBulkForm(f => ({ ...f, sms_media_url: url }))}
                      />
                    )}
                  </div>

                  <button
                    onClick={() => setBulkStep('confirm')}
                    disabled={!hasMessageContent(bulkForm) || (!bulkForm.send_email && !bulkForm.send_sms) || (bulkForm.send_email && !bulkForm.subject.trim())}
                    className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-3 rounded-lg font-medium"
                  >
                    Review Message
                  </button>
                </div>
              )}

              {/* Step 2: Confirm */}
              {bulkStep === 'confirm' && (
                <div className="space-y-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-yellow-800">
                        {retryTarget ? 'Confirm Resend' : 'Confirm Bulk Message'}
                      </p>
                      <p className="text-sm text-yellow-700 mt-1">
                        {retryTarget
                          ? `This will send only to the ${retryTarget.failed} recipients the earlier send could not reach, and cannot be undone.`
                          : 'This will send to all customers and cannot be undone.'}
                      </p>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <p className="text-sm"><strong>Channels:</strong>{' '}
                      {retryTarget
                        ? [bulkForm.send_email && 'Email', bulkForm.send_sms && 'SMS'].filter(Boolean).join(', ')
                          + ` — ${retryTarget.failed} failed recipients only`
                        : [
                            bulkForm.send_email && `Email (${emailCustomerCount})`,
                            bulkForm.send_sms && `SMS (${bulkForm.include_non_consented ? smsAllCount : smsCustomerCount})`
                          ].filter(Boolean).join(', ')}
                    </p>
                    {bulkForm.send_sms && bulkForm.include_non_consented && (
                      <p className="text-sm text-amber-700">
                        Texting all {smsAllCount} customers with a phone number, including those
                        who never opted in. "Reply STOP to opt out." is appended automatically.
                      </p>
                    )}
                    {bulkForm.send_email && <p className="text-sm"><strong>Subject:</strong> {bulkForm.subject}</p>}
                    <div>
                      <p className="text-sm font-medium mb-1">Message Preview:</p>
                      {bulkForm.send_email ? (
                        <div className="bg-white border rounded p-3 text-sm overflow-y-auto" style={{ maxHeight: '300px' }} dangerouslySetInnerHTML={{ __html: bulkForm.message }} />
                      ) : (
                        <div className="bg-white border rounded p-3 text-sm whitespace-pre-wrap overflow-y-auto" style={{ maxHeight: '300px' }}>{bulkForm.message}</div>
                      )}
                      {bulkForm.send_sms && bulkForm.sms_media_url && (
                        <div className="mt-3">
                          <p className="text-xs text-gray-600 mb-1">Image attached to the text:</p>
                          <img
                            src={bulkForm.sms_media_url}
                            alt="Attached to text message"
                            className="w-32 h-32 object-cover rounded border border-gray-300 bg-white"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setBulkStep('compose')}
                      className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-3 rounded-lg font-medium"
                    >
                      Back
                    </button>
                    <button
                      onClick={sendBulkMessage}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-lg font-medium"
                    >
                      Send Now
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Sending */}
              {bulkStep === 'sending' && (
                <div className="text-center py-8 space-y-4">
                  <Loader className="w-12 h-12 text-purple-600 animate-spin mx-auto" />
                  <p className="text-lg font-medium text-gray-800">Sending messages...</p>
                  {bulkProgress && bulkProgress.total > 0 && (
                    <div className="max-w-sm mx-auto space-y-2">
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                          className="bg-purple-600 h-2.5 rounded-full transition-all duration-300"
                          style={{ width: `${Math.round((bulkProgress.done / bulkProgress.total) * 100)}%` }}
                        />
                      </div>
                      <p className="text-sm text-gray-600">
                        {bulkProgress.done} of {bulkProgress.total} customers
                      </p>
                    </div>
                  )}
                  <p className="text-sm text-gray-500">Please do not close this window.</p>
                </div>
              )}

              {/* Step 4: Results */}
              {bulkStep === 'results' && bulkResults && (
                <div className="space-y-4">
                  {bulkResults.success ? (
                    <div className="text-center py-4">
                      <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                      <p className="text-lg font-bold text-gray-800">Messages Sent!</p>
                    </div>
                  ) : (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-red-800 font-medium">Error: {bulkResults.error}</p>
                    </div>
                  )}

                  {bulkResults.results && (
                    <div className="space-y-3">
                      {bulkForm.send_email && (
                        <div className="bg-blue-50 rounded-lg p-4">
                          <p className="font-medium text-blue-800 mb-2 flex items-center gap-2"><Mail className="w-4 h-4" /> Email Results</p>
                          <div className="grid grid-cols-3 gap-2 text-sm">
                            <div className="text-center"><span className="block text-lg font-bold text-green-600">{bulkResults.results.email.sent}</span>Sent</div>
                            <div className="text-center"><span className="block text-lg font-bold text-red-600">{bulkResults.results.email.failed}</span>Failed</div>
                            <div className="text-center"><span className="block text-lg font-bold text-gray-500">{bulkResults.results.email.skipped}</span>Skipped</div>
                          </div>
                          {bulkResults.results.email.errors?.length > 0 && (
                            <div className="mt-2 text-xs text-red-600">
                              {bulkResults.results.email.errors.map((e, i) => <p key={i}>{e}</p>)}
                            </div>
                          )}
                        </div>
                      )}
                      {bulkForm.send_sms && (
                        <div className="bg-green-50 rounded-lg p-4">
                          <p className="font-medium text-green-800 mb-2 flex items-center gap-2"><Phone className="w-4 h-4" /> SMS Results</p>
                          <div className="grid grid-cols-3 gap-2 text-sm">
                            <div className="text-center"><span className="block text-lg font-bold text-green-600">{bulkResults.results.sms.sent}</span>Sent</div>
                            <div className="text-center"><span className="block text-lg font-bold text-red-600">{bulkResults.results.sms.failed}</span>Failed</div>
                            <div className="text-center"><span className="block text-lg font-bold text-gray-500">{bulkResults.results.sms.skipped}</span>Skipped</div>
                          </div>
                          {bulkResults.results.sms.errors?.length > 0 && (
                            <div className="mt-2 text-xs text-red-600">
                              {bulkResults.results.sms.errors.map((e, i) => <p key={i}>{e}</p>)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    onClick={() => setShowBulkMessage(false)}
                    className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-3 rounded-lg font-medium"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Customer Modal */}
      {showAddCustomerForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-800">Add New Customer</h3>
              <button
                onClick={() => {
                  setShowAddCustomerForm(false);
                }}
                className="text-gray-400 hover:text-gray-600"
                disabled={isSubmitting}
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <CustomerForm
                onSave={handleAddNewCustomer}
                onCancel={() => setShowAddCustomerForm(false)}
                isSubmitting={isSubmitting}
              />
            </div>
          </div>
        </div>
      )}

      {/* Import CSV Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-indigo-600 text-white p-4 flex justify-between items-center rounded-t-xl z-10">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Import Customers from CSV
              </h3>
              {importStep !== 'importing' && (
                <button onClick={() => setShowImportModal(false)} className="text-white hover:text-gray-200">
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            <div className="p-6">
              {/* Step 1: Select file */}
              {importStep === 'select' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Upload a CSV file with your customers. The file should include a header row with
                    <strong> Name</strong>, <strong>Phone</strong>, <strong>Email</strong>, and/or <strong>Address</strong> columns.
                    Rows are matched by email or phone, and existing customers are skipped automatically.
                  </p>

                  {importError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{importError}</span>
                    </div>
                  )}

                  <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-8 cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition-colors">
                    <Upload className="w-10 h-10 text-gray-400 mb-3" />
                    <span className="text-sm font-medium text-gray-700">Click to choose a CSV file</span>
                    <span className="text-xs text-gray-400 mt-1">.csv files only</span>
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      onChange={handleImportFile}
                      className="hidden"
                    />
                  </label>
                </div>
              )}

              {/* Step 2: Preview */}
              {importStep === 'preview' && (
                <div className="space-y-4">
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm text-indigo-800">
                    Found <strong>{importRows.length}</strong> customer{importRows.length === 1 ? '' : 's'} in
                    <strong> {importFileName}</strong>. Review below, then import.
                  </div>

                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="max-h-72 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium text-gray-600">Name</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600">Phone</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600">Email</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600">Address</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importRows.slice(0, 100).map((row, i) => (
                            <tr key={i} className="border-t border-gray-100">
                              <td className="px-3 py-2">{row.name || <span className="text-gray-400 italic">—</span>}</td>
                              <td className="px-3 py-2">{row.phone || <span className="text-gray-400 italic">—</span>}</td>
                              <td className="px-3 py-2">{row.email || <span className="text-gray-400 italic">—</span>}</td>
                              <td className="px-3 py-2">{row.address || <span className="text-gray-400 italic">—</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {importRows.length > 100 && (
                    <p className="text-xs text-gray-500">Showing first 100 of {importRows.length} rows. All will be imported.</p>
                  )}

                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      onClick={() => setImportStep('select')}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Back
                    </button>
                    <button
                      onClick={runImport}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      Import {importRows.length} Customer{importRows.length === 1 ? '' : 's'}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Importing */}
              {importStep === 'importing' && (
                <div className="py-12 text-center">
                  <Loader className="w-10 h-10 text-indigo-600 animate-spin mx-auto mb-4" />
                  <p className="text-gray-600">Importing {importRows.length} customers...</p>
                </div>
              )}

              {/* Step 4: Results */}
              {importStep === 'results' && importResults && (
                <div className="space-y-4">
                  {importResults.success ? (
                    <>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-green-800">Import complete</p>
                          <p className="text-sm text-green-700 mt-1">
                            {importResults.imported} added · {importResults.skipped} skipped · {importResults.total} total rows
                          </p>
                        </div>
                      </div>

                      {Array.isArray(importResults.details) && importResults.details.some(d => d.status !== 'imported') && (
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-gray-50 px-3 py-2 text-sm font-medium text-gray-600">Skipped rows</div>
                          <div className="max-h-48 overflow-y-auto">
                            <table className="w-full text-sm">
                              <tbody>
                                {importResults.details.filter(d => d.status !== 'imported').map((d, i) => (
                                  <tr key={i} className="border-t border-gray-100">
                                    <td className="px-3 py-2 text-gray-700">Row {d.row}{d.name ? ` · ${d.name}` : ''}</td>
                                    <td className="px-3 py-2 text-gray-500">{d.reason}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                      <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-red-800">Import failed</p>
                        <p className="text-sm text-red-700 mt-1">{importResults.error || 'Unknown error'}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      onClick={() => setShowImportModal(false)}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Single Message Modal */}
      {showSingleMessage && singleMessageTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-green-600 text-white p-4 flex justify-between items-center rounded-t-xl z-10">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Send className="w-5 h-5" />
                Message {singleMessageTarget.customer_name}
              </h3>
              {singleStep !== 'sending' && (
                <button onClick={() => setShowSingleMessage(false)} className="text-white hover:text-gray-200">
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            <div className="p-6">
              {singleStep === 'compose' && (
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-3 text-sm">
                    <p><strong>{singleMessageTarget.customer_name}</strong></p>
                    {singleMessageTarget.customer_email && <p className="text-gray-600 flex items-center gap-1"><Mail className="w-3 h-3" /> {singleMessageTarget.customer_email}</p>}
                    {singleMessageTarget.customer_phone && <p className="text-gray-600 flex items-center gap-1"><Phone className="w-3 h-3" /> {singleMessageTarget.customer_phone}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Send via:</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={singleForm.send_email}
                          onChange={e => setSingleForm(f => ({ ...f, send_email: e.target.checked }))}
                          className="w-4 h-4 text-green-600 rounded"
                          disabled={!singleMessageTarget.customer_email}
                        />
                        <Mail className="w-4 h-4 text-gray-600" />
                        <span className="text-sm">Email</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={singleForm.send_sms}
                          onChange={e => setSingleForm(f => ({ ...f, send_sms: e.target.checked }))}
                          className="w-4 h-4 text-green-600 rounded"
                          disabled={!singleMessageTarget.customer_phone}
                        />
                        <Phone className="w-4 h-4 text-gray-600" />
                        <span className="text-sm">SMS</span>
                      </label>
                    </div>
                  </div>

                  {singleForm.send_email && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                      <input
                        type="text"
                        value={singleForm.subject}
                        onChange={e => setSingleForm(f => ({ ...f, subject: e.target.value }))}
                        placeholder="e.g. Special Offer from AllTech LLC"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                  )}

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">Message</label>
                      {singleForm.send_email && (
                        <button
                          type="button"
                          onClick={() => setSingleHtmlMode(m => !m)}
                          className={`flex items-center gap-1 text-xs px-2 py-1 rounded font-medium ${singleHtmlMode ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                        >
                          <Code className="w-3 h-3" />
                          {singleHtmlMode ? 'Rich Editor' : 'HTML Code'}
                        </button>
                      )}
                    </div>
                    {singleForm.send_email ? (
                      <div className="bulk-email-editor">
                        {singleHtmlMode ? (
                          <textarea
                            value={singleForm.message}
                            onChange={e => setSingleForm(f => ({ ...f, message: e.target.value }))}
                            placeholder="Paste your HTML code here..."
                            rows={12}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            style={{ minHeight: '250px', tabSize: 2 }}
                          />
                        ) : (
                          <ReactQuill
                            ref={singleQuillRef}
                            theme="snow"
                            value={singleForm.message}
                            onChange={val => setSingleForm(f => ({ ...f, message: val }))}
                            placeholder="Compose your email message..."
                            modules={singleQuillModules}
                            formats={quillFormats}
                            style={{ minHeight: '200px', background: '#fff', borderRadius: '0 0 8px 8px' }}
                          />
                        )}
                        {singleForm.send_sms && (
                          <p className="text-xs mt-1 text-orange-600">Note: SMS will receive a plain-text version.</p>
                        )}
                      </div>
                    ) : (
                      <>
                        <textarea
                          value={singleForm.message}
                          onChange={e => setSingleForm(f => ({ ...f, message: e.target.value }))}
                          placeholder="Type your message here..."
                          rows={6}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                        {singleForm.send_sms && (
                          <p className={`text-xs mt-1 ${singleForm.message.length > 160 ? 'text-orange-600' : 'text-gray-500'}`}>
                            {singleForm.message.length}/160 characters {singleForm.message.length > 160 ? `(${Math.ceil(singleForm.message.length / 160)} SMS segments)` : ''}
                          </p>
                        )}
                      </>
                    )}

                    <button
                      type="button"
                      onClick={() => insertSchedulerLink(setSingleForm, singleForm.send_email)}
                      className="mt-2 inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
                    >
                      <Calendar className="w-3 h-3" />
                      Add booking link
                    </button>

                    {singleForm.send_sms && (
                      <SmsImagePicker
                        value={singleForm.sms_media_url}
                        onChange={url => setSingleForm(f => ({ ...f, sms_media_url: url }))}
                      />
                    )}
                  </div>

                  <button
                    onClick={() => setSingleStep('confirm')}
                    disabled={!hasMessageContent(singleForm) || (!singleForm.send_email && !singleForm.send_sms) || (singleForm.send_email && !singleForm.subject.trim())}
                    className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-3 rounded-lg font-medium"
                  >
                    Review Message
                  </button>
                </div>
              )}

              {singleStep === 'confirm' && (
                <div className="space-y-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-yellow-800">Confirm Message</p>
                      <p className="text-sm text-yellow-700 mt-1">Send to <strong>{singleMessageTarget.customer_name}</strong></p>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <p className="text-sm"><strong>Channels:</strong>{' '}
                      {[singleForm.send_email && 'Email', singleForm.send_sms && 'SMS'].filter(Boolean).join(', ')}
                    </p>
                    {singleForm.send_email && <p className="text-sm"><strong>Subject:</strong> {singleForm.subject}</p>}
                    <div>
                      <p className="text-sm font-medium mb-1">Message Preview:</p>
                      {singleForm.send_email ? (
                        <div className="bg-white border rounded p-3 text-sm overflow-y-auto" style={{ maxHeight: '300px' }} dangerouslySetInnerHTML={{ __html: singleForm.message }} />
                      ) : (
                        <div className="bg-white border rounded p-3 text-sm whitespace-pre-wrap overflow-y-auto" style={{ maxHeight: '300px' }}>{singleForm.message}</div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setSingleStep('compose')}
                      className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-3 rounded-lg font-medium"
                    >
                      Back
                    </button>
                    <button
                      onClick={sendSingleMessage}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg font-medium"
                    >
                      Send Now
                    </button>
                  </div>
                </div>
              )}

              {singleStep === 'sending' && (
                <div className="text-center py-8 space-y-4">
                  <Loader className="w-12 h-12 text-green-600 animate-spin mx-auto" />
                  <p className="text-lg font-medium">Sending message...</p>
                </div>
              )}

              {singleStep === 'results' && singleResults && (
                <div className="space-y-4">
                  {singleResults.success ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-green-800">Message Sent!</p>
                        <p className="text-sm text-green-700 mt-1">{singleResults.message}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-red-800">Error</p>
                        <p className="text-sm text-red-700 mt-1">{singleResults.error}</p>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => setShowSingleMessage(false)}
                    className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-3 rounded-lg font-medium"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Customer Modal */}
      {showEditCustomerForm && editingCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-800">Edit Customer</h3>
              <button
                onClick={() => {
                  setShowEditCustomerForm(false);
                  setEditingCustomer(null);
                }}
                className="text-gray-400 hover:text-gray-600"
                disabled={isSubmitting}
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <CustomerForm
                customer={editingCustomer}
                onSave={handleUpdateExistingCustomer}
                onCancel={() => {
                  setShowEditCustomerForm(false);
                  setEditingCustomer(null);
                }}
                isSubmitting={isSubmitting}
              />
            </div>
          </div>
        </div>
      )}

      {/* Edit Customer Modal - Address Management */}
      {editingCustomer && !showEditCustomerForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-gray-800">Manage Customer Addresses</h3>
                <p className="text-sm text-gray-600 mt-1">{editingCustomer.customer_name}</p>
              </div>
              <button
                onClick={() => {
                  setEditingCustomer(null);
                  setCustomerAddresses([]);
                  setAppointmentAddresses([]);
                  setCustomerACUnits([]);
                  setShowAddressForm(false);
                  setEditingAddress(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {/* Customer Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Email</label>
                    <p className={editingCustomer.customer_email ? 'text-gray-800' : 'text-gray-400 italic'}>
                      {editingCustomer.customer_email || 'No email'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Phone</label>
                    <p className="text-gray-800">{editingCustomer.customer_phone}</p>
                  </div>
                </div>
              </div>

              {/* Original Appointment Addresses */}
              {appointmentAddresses.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Original Booking Addresses
                  </h4>
                  <div className="space-y-2">
                    {appointmentAddresses.map((item, index) => (
                      <div key={index} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-start gap-3">
                          <MapPin className="w-4 h-4 text-blue-600 mt-1 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-gray-800 font-medium">{item.address}</p>
                            <div className="flex gap-3 mt-1 text-xs text-gray-600">
                              <span>First used: {new Date(item.appointment_date).toLocaleDateString()}</span>
                              <span>•</span>
                              <span>{item.service_name}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AC Units */}
              {customerACUnits.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Wind className="w-4 h-4" />
                    AC Units
                  </h4>
                  <div className="space-y-2">
                    {customerACUnits.map((unit, index) => (
                      <div key={index} className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Wind className="w-4 h-4 text-green-600" />
                              <span className="font-medium text-gray-800">
                                {unit.manufacturer || 'Unknown'} {unit.model_number}
                              </span>
                            </div>

                            {(unit.height_inches && unit.width_inches) ? (
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <Ruler className="w-3 h-3" />
                                <span>Dimensions: H: {unit.height_inches}" × W: {unit.width_inches}"</span>
                                {unit.depth_inches && <span>× D: {unit.depth_inches}"</span>}
                              </div>
                            ) : unit.tonnage ? (
                              <div className="text-sm text-gray-700">{unit.tonnage} Ton</div>
                            ) : null}

                            {unit.unit_type && (
                              <div className="text-xs text-gray-600 mt-1">Type: {unit.unit_type}</div>
                            )}

                            {unit.service_address && (
                              <div className="text-xs text-gray-600 mt-1">
                                <MapPin className="w-3 h-3 inline mr-1" />
                                {unit.service_address}
                              </div>
                            )}
                          </div>

                          {unit.image_path && (
                            <div className="ml-3 flex-shrink-0">
                              <img
                                src={`/scheduler/${unit.image_path}`}
                                alt="AC Unit"
                                className="w-16 h-16 object-cover rounded border border-gray-200"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Additional Addresses List */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-semibold text-gray-700">Service Addresses</h4>
                  {!showAddressForm && (
                    <button
                      onClick={() => {
                        setShowAddressForm(true);
                        setEditingAddress(null);
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Address
                    </button>
                  )}
                </div>

                {customerAddresses.length === 0 && !showAddressForm ? (
                  <div className="text-center py-8 text-gray-500">
                    <Home className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No additional addresses saved</p>
                    <p className="text-sm mt-1">Click "Add Address" to add multiple properties</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {customerAddresses.map((address) => (
                      <div key={address.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <MapPin className="w-4 h-4 text-gray-400" />
                              {address.address_label && (
                                <span className="font-medium text-gray-800">{address.address_label}</span>
                              )}
                              {address.is_primary && (
                                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Primary</span>
                              )}
                            </div>
                            <p className="text-gray-700">{address.street_address}</p>
                            <p className="text-gray-600 text-sm">{address.city}, {address.state} {address.zip_code}</p>
                            {address.notes && (
                              <p className="text-gray-500 text-sm mt-2 italic">{address.notes}</p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setEditingAddress(address);
                                setShowAddressForm(true);
                              }}
                              className="text-blue-600 hover:text-blue-800 p-2"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteAddress(address.id)}
                              className="text-red-600 hover:text-red-800 p-2"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Address Form */}
              {showAddressForm && (
                <AddressForm
                  address={editingAddress}
                  onSave={handleSaveAddress}
                  onCancel={() => {
                    setShowAddressForm(false);
                    setEditingAddress(null);
                  }}
                  isSubmitting={isSubmitting}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Customer Form Component
const CustomerForm = ({ customer, onSave, onCancel, isSubmitting }) => {
  const [formData, setFormData] = useState({
    id: customer?.id || null,
    first_name: customer?.first_name || '',
    last_name: customer?.last_name || '',
    email: customer?.email || customer?.customer_email || '',
    // Set when the office genuinely has no email for this customer. The API
    // stores NULL rather than '' - email carries a unique key, so blanks would
    // collide with each other.
    no_email: !!customer && !(customer.email || customer.customer_email),
    phone: customer?.phone || customer?.customer_phone || '',
    address_line1: customer?.address_line1 || '',
    address_line2: customer?.address_line2 || '',
    city: customer?.city || '',
    state: customer?.state || '',
    zip: customer?.zip || '',
    ac_unit_height: customer?.ac_unit_height || '',
    ac_unit_width: customer?.ac_unit_width || '',
    notes: customer?.notes || ''
  });

  // Phone number formatting
  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)})${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)})${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData(prev => ({ ...prev, phone: formatted }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      alert('Please provide both first and last name');
      return;
    }

    if (!formData.phone.trim()) {
      alert('Please provide a phone number');
      return;
    }

    if (!formData.no_email) {
      if (!formData.email.trim()) {
        alert('Please provide an email address, or check "No email address"');
        return;
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        alert('Please provide a valid email address');
        return;
      }
    }

    onSave(formData.no_email ? { ...formData, email: '' } : formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            First Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.first_name}
            onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
            required
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            disabled={isSubmitting}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Last Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.last_name}
            onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
            required
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            disabled={isSubmitting}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email {!formData.no_email && <span className="text-red-500">*</span>}
          </label>
          <input
            type="email"
            value={formData.no_email ? '' : formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            required={!formData.no_email}
            placeholder={formData.no_email ? 'No email on file' : ''}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 disabled:bg-gray-100"
            disabled={isSubmitting || formData.no_email}
          />
          <label className="flex items-center gap-2 mt-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={formData.no_email}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                no_email: e.target.checked,
                email: e.target.checked ? '' : prev.email
              }))}
              disabled={isSubmitting}
            />
            No email address for this customer
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phone <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={handlePhoneChange}
            required
            placeholder="(208)555-1234"
            maxLength={13}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            disabled={isSubmitting}
          />
          <p className="text-xs text-gray-500 mt-1">Format: (123)456-7890</p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <MapPin className="w-4 h-4 inline mr-1" />
          Address
        </label>
        <div className="space-y-3">
          <input
            type="text"
            value={formData.address_line1}
            onChange={(e) => setFormData(prev => ({ ...prev, address_line1: e.target.value }))}
            placeholder="Address Line 1"
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            disabled={isSubmitting}
          />
          <input
            type="text"
            value={formData.address_line2}
            onChange={(e) => setFormData(prev => ({ ...prev, address_line2: e.target.value }))}
            placeholder="Address Line 2 (Optional)"
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            disabled={isSubmitting}
          />
          <div className="grid grid-cols-6 gap-3">
            <div className="col-span-3">
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                placeholder="City"
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                disabled={isSubmitting}
              />
            </div>
            <div className="col-span-1">
              <input
                type="text"
                value={formData.state}
                onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value.toUpperCase() }))}
                placeholder="ST"
                maxLength={2}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 uppercase"
                disabled={isSubmitting}
              />
            </div>
            <div className="col-span-2">
              <input
                type="text"
                value={formData.zip}
                onChange={(e) => setFormData(prev => ({ ...prev, zip: e.target.value.replace(/\D/g, '').slice(0, 5) }))}
                placeholder="ZIP"
                maxLength={5}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                disabled={isSubmitting}
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <Wind className="w-4 h-4 inline mr-1" />
          AC Unit Dimensions (Optional)
        </label>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Height (inches)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.ac_unit_height}
              onChange={(e) => setFormData(prev => ({ ...prev, ac_unit_height: e.target.value }))}
              placeholder="24.5"
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              disabled={isSubmitting}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Width (inches)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.ac_unit_width}
              onChange={(e) => setFormData(prev => ({ ...prev, ac_unit_width: e.target.value }))}
              placeholder="30.0"
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              disabled={isSubmitting}
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notes
        </label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
          rows={3}
          placeholder="Any additional notes about this customer..."
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
          disabled={isSubmitting}
        />
      </div>

      <div className="flex gap-3 pt-4">
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium"
        >
          {isSubmitting ? 'Saving...' : (customer ? 'Update Customer' : 'Add Customer')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="flex-1 bg-gray-300 hover:bg-gray-400 disabled:opacity-50 text-gray-700 px-4 py-2 rounded-lg font-medium"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

// Address Form Component
const AddressForm = ({ address, onSave, onCancel, isSubmitting }) => {
  const [formData, setFormData] = useState({
    address_label: address?.address_label || '',
    street_address: address?.street_address || '',
    city: address?.city || '',
    state: address?.state || '',
    zip_code: address?.zip_code || '',
    is_primary: address?.is_primary || false,
    notes: address?.notes || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <h5 className="font-semibold text-blue-800 mb-4">
        {address ? 'Edit Address' : 'Add New Address'}
      </h5>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Address Label (Optional)
          </label>
          <input
            type="text"
            value={formData.address_label}
            onChange={(e) => setFormData(prev => ({ ...prev, address_label: e.target.value }))}
            placeholder="e.g., Main Property, Summer House, Rental"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Street Address *</label>
          <input
            type="text"
            value={formData.street_address}
            onChange={(e) => setFormData(prev => ({ ...prev, street_address: e.target.value }))}
            required
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
            <input
              type="text"
              value={formData.state}
              onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
              required
              maxLength={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ZIP *</label>
            <input
              type="text"
              value={formData.zip_code}
              onChange={(e) => setFormData(prev => ({ ...prev, zip_code: e.target.value }))}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={formData.is_primary}
              onChange={(e) => setFormData(prev => ({ ...prev, is_primary: e.target.checked }))}
              className="rounded text-green-600"
            />
            <span className="font-medium text-gray-700">Set as primary address</span>
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            rows={2}
            placeholder="Additional details about this property..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium"
          >
            {isSubmitting ? 'Saving...' : (address ? 'Update Address' : 'Add Address')}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="flex-1 bg-gray-300 hover:bg-gray-400 disabled:opacity-50 text-gray-700 px-4 py-2 rounded-lg font-medium"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminCustomers;
